// process.env safe client component
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { RefreshCw, Sparkles, ChevronDown, ChevronUp, X, ShieldCheck, Zap } from 'lucide-react';

interface ReleaseInfo {
  version: string;
  title: string;
  releaseNotes?: string;
  status: string;
  isBeta?: boolean;
  releasedAt?: string;
}

function parseSemver(v: string): number[] {
  const clean = (v || '').replace(/^[^\d]*/, '');
  return clean.split('.').map(n => parseInt(n, 10) || 0);
}

function compareSemver(vA: string, vB: string): number {
  if (!vA || !vB) return 0;
  const a = parseSemver(vA);
  const b = parseSemver(vB);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const numA = a[i] || 0;
    const numB = b[i] || 0;
    if (numA > numB) return 1;
    if (numA < numB) return -1;
  }
  return 0;
}

export interface VersionUpdateNotifierProps {
  tenantId?: string | number;
  token?: string;
  isTenantView?: boolean;
}

export const VersionUpdateNotifier: React.FC<VersionUpdateNotifierProps> = ({ tenantId: propTenantId, token: propToken }) => {
  const activeToken = propToken || (typeof window !== 'undefined' ? localStorage.getItem('tenet_auth_token') || sessionStorage.getItem('adminToken') : null);

  // ZERO LEAK: If no active authenticated session exists, NEVER run or render
  if (!activeToken) {
    return null;
  }

  // Resolve tenant ID for strict per-tenant version isolation
  const effectiveTenantId = propTenantId || (() => {
    try {
      if (activeToken && !activeToken.startsWith('TENET_')) {
        const decoded = JSON.parse(atob(activeToken.split('.')[1]));
        return decoded?.tenantId || null;
      }
    } catch {}
    return null;
  })();

  const storageKey = effectiveTenantId ? `kangavena_running_version_tenant_${effectiveTenantId}` : 'kangavena_running_version_admin';

  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [latestRelease, setLatestRelease] = useState<ReleaseInfo | null>(null);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isDismissed, setIsDismissed] = useState<boolean>(false);

  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkVersion = useCallback(async () => {
    try {
      const isBetaPreview = localStorage.getItem('kangavena_beta_preview') === '1';
      const headers: Record<string, string> = {
        'Cache-Control': 'no-cache',
      };
      if (isBetaPreview) {
        headers['x-beta-preview'] = '1';
      }
      if (activeToken) {
        headers['Authorization'] = activeToken.startsWith('Bearer ') ? activeToken : `Bearer ${activeToken}`;
        if (activeToken.startsWith('TENET_')) {
          headers['x-tenant-api-key'] = activeToken;
        }
      }

      const res = await fetch('/api/system/releases/active', {
        method: 'GET',
        headers,
        cache: 'no-store',
      });

      if (!res.ok) return;

      const data = await res.json();
      if (!data.success || !data.version) return;

      // On first load, resolve this specific tenant's running version
      if (!currentVersion) {
        const storedRunningVersion = data.tenantRunningVersion || sessionStorage.getItem(storageKey);
        if (storedRunningVersion) {
          setCurrentVersion(storedRunningVersion);
          if (compareSemver(data.version, storedRunningVersion) > 0) {
            setLatestRelease(data);
            setIsUpdateAvailable(true);
          }
        } else {
          // Pin initial version to tenant-scoped storage
          const initialVer = data.tenantRunningVersion || data.version;
          sessionStorage.setItem(storageKey, initialVer);
          setCurrentVersion(initialVer);
        }
      } else {
        // Subsequent checks: update is ONLY available if data.version is strictly higher than this tenant's running version
        if (compareSemver(data.version, currentVersion) > 0) {
          const dismissKey = effectiveTenantId ? `dismissed_update_${effectiveTenantId}_${data.version}` : `dismissed_update_${data.version}`;
          const dismissedUntil = localStorage.getItem(dismissKey);
          if (dismissedUntil && Date.now() < Number(dismissedUntil)) {
            setIsDismissed(true);
          } else {
            setIsDismissed(false);
          }
          setLatestRelease(data);
          setIsUpdateAvailable(true);
        } else {
          setIsUpdateAvailable(false);
        }
      }
    } catch (_) {
      // Non-blocking network check
    }
  }, [currentVersion, activeToken, effectiveTenantId, storageKey]);

  useEffect(() => {
    checkVersion();
    // Poll every 45 seconds for seamless release discovery
    checkIntervalRef.current = setInterval(checkVersion, 45000);

    // Also check immediately when window gains focus
    const handleFocus = () => checkVersion();
    window.addEventListener('focus', handleFocus);

    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkVersion]);

  const handleApplyUpdate = async () => {
    setIsUpdating(true);
    const targetVer = latestRelease?.version;
    if (targetVer) {
      sessionStorage.setItem(storageKey, targetVer);
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (activeToken) {
          headers['Authorization'] = activeToken.startsWith('Bearer ') ? activeToken : `Bearer ${activeToken}`;
          if (activeToken.startsWith('TENET_')) {
            headers['x-tenant-api-key'] = activeToken;
          }
        }
        await fetch('/api/system/releases/tenant-set-version', {
          method: 'POST',
          headers,
          body: JSON.stringify({ version: targetVer, tenantId: effectiveTenantId }),
        }).catch(() => {});
      } catch (_) {}
    }
    setTimeout(() => {
      window.location.reload();
    }, 400);
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    if (latestRelease?.version) {
      // Dismiss for 1 hour for this specific tenant
      const dismissKey = effectiveTenantId ? `dismissed_update_${effectiveTenantId}_${latestRelease.version}` : `dismissed_update_${latestRelease.version}`;
      localStorage.setItem(dismissKey, String(Date.now() + 60 * 60 * 1000));
    }
  };

  if (!isUpdateAvailable || isDismissed || !latestRelease) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.95 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="fixed bottom-6 right-6 z-[9999] max-w-sm w-full pointer-events-auto"
      >
        <div className="relative overflow-hidden rounded-2xl border border-cyan-500/40 bg-slate-950/90 p-4 shadow-[0_10px_40px_rgba(6,182,212,0.3)] backdrop-blur-xl">
          {/* Ambient Glow */}
          <div className="absolute -top-10 -right-10 w-24 h-24 bg-cyan-500/20 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-emerald-500/20 rounded-full blur-2xl pointer-events-none" />

          {/* Top Bar */}
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <span className="flex h-2.5 w-2.5 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500" />
              </span>
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-[10px] font-mono font-bold tracking-wider text-cyan-300 uppercase">
                <Sparkles className="w-3 h-3 text-cyan-400" />
                {latestRelease.isBeta ? 'Canary Beta Available' : 'New Version Available'}
              </div>
            </div>

            <button
              onClick={handleDismiss}
              className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
              title="Dismiss for now"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Version & Title */}
          <div className="mb-3">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-sm font-black text-cyan-400">
                {latestRelease.version}
              </span>
              <span className="text-xs text-slate-300 font-semibold line-clamp-1">
                {latestRelease.title}
              </span>
            </div>
            <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
              A smooth zero-downtime release is ready. Update now to access the latest features and optimizations.
            </p>
          </div>

          {/* Collapsible Release Notes */}
          {latestRelease.releaseNotes && (
            <div className="mb-3">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-1 text-[11px] font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                <span>{isExpanded ? 'Hide changes' : 'View changes'}</span>
                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>

              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2 p-2.5 rounded-xl bg-slate-900/80 border border-white/5 text-[11px] font-mono text-slate-300 whitespace-pre-wrap max-h-36 overflow-y-auto"
                >
                  {latestRelease.releaseNotes}
                </motion.div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleApplyUpdate}
              disabled={isUpdating}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-slate-950 font-bold text-xs shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all disabled:opacity-50 cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isUpdating ? 'animate-spin' : ''}`} />
              <span>{isUpdating ? 'Applying Update...' : 'Update Now (1-Click)'}</span>
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-2 rounded-xl border border-white/10 hover:bg-white/5 text-slate-400 hover:text-white text-xs font-semibold transition-colors"
            >
              Later
            </button>
          </div>

          {/* Zero Downtime Guarantee Footer */}
          <div className="flex items-center justify-between text-[9px] text-slate-500 mt-2 font-mono">
            <span className="flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-emerald-400" />
              Zero-Downtime Atomic Swap
            </span>
            <span>Current: {currentVersion || 'v6.5.0'}</span>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VersionUpdateNotifier;
